import { ECardinality, ESdChangeReason, ISymbol, IWFParameter } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { ChangeEvent } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { WithForm } from "../../../../shared/hoc/with-form";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { SymbolPicker } from "../../right-panel/properties-panel/symbol-picker";
import "./workflow-parameter-item-create.scss";
import { WORKFLOW_SCALAR_TYPES } from "./workflow-parameter-type-options";

/** CREATE adds a parameter; it does not save a document. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

const WORKFLOW_RESERVED_PARAM_NAMES = [
  "id",
  "typename",
  "name",
  "eventname",
  "eventkey",
  "effectivedate",
  "assignmenttype",
  "assignmentkey",
  "description",
  "duedate"
];

export interface IWorkflowParameterItemCreateState {
  /** Where CREATE stands. Owned by `SaveStatus`, never set directly. */
  createStatus: KuikaButtonStatus;
}

export interface IWorkflowParameterItemCreateProps {
  parameter: IWFParameter;
  handleChange: (e: any, type: string) => void;
  /** Resolves `true` once the parameter has been saved, `false` when a check refused it. */
  addField: (paramToAdd: IWFParameter) => Promise<boolean>;
  /** The saved parameter has held its ✓ on CREATE; the form can go. */
  onCreated: () => void;
  onCancel: () => void;
}

class WorkflowParameterItemCreate extends PureSdComponent<
  IWorkflowParameterItemCreateProps & IWithSDProps & IWithContextProp,
  IWorkflowParameterItemCreateState
> {
  /**
   * CREATE's state machine: the new parameter's card appears among the saved ones, and the form
   * holds its ✓ on CREATE before it goes. `canStart` keeps a second CREATE (a duplicate) out.
   */
  private readonly createStatus: SaveStatus = new SaveStatus((createStatus: KuikaButtonStatus): void => {
    this.setState({ createStatus });
  });

  constructor(props: IWorkflowParameterItemCreateProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      createStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.createStatus.dispose();
  }

  addNewParam = async (paramToAdd: IWFParameter) => {
    const name = (paramToAdd.name ?? "").toLowerCase();
    const regex = new RegExp("^[A-Za-z][A-Za-z0-9]+$");
    const charCheck = regex.test(name);
    if (!charCheck) {
      return;
    }
    if (name === "" || name.includes(" ")) {
      return;
    }
    const reserved = [...(this.props.sd.project?.commonReservedKeys ?? []), ...WORKFLOW_RESERVED_PARAM_NAMES].map((x) =>
      x.toLowerCase()
    );
    if (reserved.includes(name) || !this.createStatus.canStart) {
      return;
    }
    this.createStatus.start();
    try {
      const created: boolean = await this.props.addField(paramToAdd);
      if (created) {
        this.createStatus.succeed(this.props.onCreated);
      } else {
        // A check refused it and its warning said why; the form stays as it was.
        this.createStatus.reset();
      }
    } catch (error: unknown) {
      this.createStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  render() {
    let { parameter, handleChange, addField, onCancel } = this.props;
    return (
      <KuikaCollapsePanel
        key={`workflow-param-create-collapse-${parameter.id}`}
        // Shares the parameter card's class so the create form sits on the same gutter and
        // two-line header metrics as the saved parameters it appears among.
        className="kuika-workflow-parameters__item-collapse"
        iconPlacement="right"
        activeKey={["add"]}
        items={[
          {
            key: "add",
            className: "kuika-workflow-parameters-item__panel",
            label: (
              <div id={`param-create-header`} className="parameters-item">
                <span id={`param-create-name-header`} className="parameters-item__name">
                  Parameter Name
                </span>
                <span id={`param-create-type-header`} className="parameters-item__type">
                  Parameter Type
                </span>
              </div>
            ),
            children: (
              <div id={`param-create-content`}>
                <Form id={`param-create-form`}>
                  <span id={`param-create-name-label`} className="parameters-fields__name">
                    Name
                  </span>
                  <Form.Item
                    id={`param-create-name-form-item`}
                    name="name"
                    rules={[
                      { required: true, message: "A name is required!" },
                      {
                        validator: (_, value) => {
                          if (value.includes(" ")) {
                            return Promise.reject(new Error("Spaces are not allowed"));
                          }
                          return Promise.resolve();
                        }
                      },
                      {
                        validator: (_, value) => {
                          const reserved = [
                            ...(this.props.sd.project?.commonReservedKeys ?? []),
                            ...WORKFLOW_RESERVED_PARAM_NAMES
                          ].map((x) => x.toLowerCase());
                          if (value && reserved.includes(String(value).toLowerCase())) {
                            return Promise.reject(
                              new Error("C# reserved keywords and system names cannot be used as parameter names.")
                            );
                          }
                          return Promise.resolve();
                        }
                      },
                      { pattern: /^[A-Za-z][A-Za-z0-9]+$/, message: "Field accepts only english letters." }
                    ]}
                  >
                    <KuikaInput
                      data-testid="workflowParameterNameInput"
                      id={`param-create-name-input`}
                      className="parameters-fields__input"
                      value={parameter.name}
                      placeholder="Parameter"
                      onInput={(e) => handleChange(e, "name")}
                      autoFocus
                    />
                  </Form.Item>
                  <span id={`param-create-type-label`} className="parameters-fields__name">
                    Type
                  </span>
                  <KuikaSelectbox
                    data-testid="paramTypeSelectDropdown"
                    id={`param-create-type-select`}
                    searchable
                    style={{ width: "100%" }}
                    className="parameters-fields__input"
                    onChange={(e) => handleChange(e, "type")}
                    placeholder="Type"
                    value={parameter.dataType}
                    options={WORKFLOW_SCALAR_TYPES.map((item: string): KuikaSelectboxOption => ({
                      value: item,
                      label: item,
                      testId: `${item}Type`
                    }))}
                  />
                  {parameter.isBusinessObject && (
                    /*
                     * The two ticks keep their names in the sibling span rather than on the control, so
                     * they pass `ariaLabel` — the kit's prop for exactly that — instead of a `label`.
                     * The kit checkbox takes neither `id` nor `style`; the row's layout moved to a class
                     * and the ids it carried were referenced by nothing.
                     */
                    <div className="kuika-workflow-param-create__toggles">
                      <div id={`param-create-input-checkbox-container`} className="kuika-workflow-param-create__toggle">
                        <span id={`param-create-input-label`} className="parameters-fields__name">
                          Process Input Parameter
                        </span>
                        <KuikaCheckbox
                          dataTestId="processInputParameterCheckbox"
                          ariaLabel="Process Input Parameter"
                          className="kuika-workflow-param-create__toggle-box"
                          checked={parameter.isInput === true}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, "input")}
                        />
                      </div>
                      <div
                        id={`param-create-output-checkbox-container`}
                        className="kuika-workflow-param-create__toggle"
                      >
                        <span id={`param-create-output-label`} className="parameters-fields__name">
                          Process Output Parameter
                        </span>
                        <KuikaCheckbox
                          dataTestId="processOutputParameterCheckbox"
                          ariaLabel="Process Output Parameter"
                          className="kuika-workflow-param-create__toggle-box"
                          checked={parameter.isOutput === true}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, "output")}
                        />
                      </div>
                    </div>
                  )}
                  <span
                    id={`param-create-default-label`}
                    className="parameters-fields__name"
                    style={{ display: "flex", paddingBottom: 4, paddingTop: 4 }}
                  >
                    Default Value
                  </span>
                  <SymbolPicker
                    key={`param-create-default-value`}
                    symbol={parameter.defaultValue}
                    changeType="onBlur"
                    valueType={parameter.dataType}
                    hideWFParams={true}
                    targetCardinality={ECardinality.One}
                    hideCategoryChange={true}
                    onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason | undefined) => {
                      handleChange(symbol, "default");
                    }}
                  />
                  <div id={`param-create-buttons-container`} className="parameters-fields__buttons">
                    <KuikaButton
                      data-testid="addParamAreaCancelButton"
                      id={`param-create-cancel-button`}
                      onClick={onCancel}
                      className="parameters-fields__remove-button"
                      type="text"
                    >
                      CANCEL
                    </KuikaButton>
                    <KuikaButton
                      data-testid="addParamAreaCreateButton"
                      id={`param-create-submit-button`}
                      type="text"
                      status={this.state.createStatus}
                      statusLabels={CREATE_STATUS_LABELS}
                      onClick={() => this.addNewParam(parameter)}
                    >
                      CREATE
                    </KuikaButton>
                  </div>
                </Form>
              </div>
            )
          }
        ]}
      />
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const workflowParameterItemCreate = withContext<IWorkflowParameterItemCreateProps>(
  WithForm<IWorkflowParameterItemCreateProps>(withSD<IWorkflowParameterItemCreateProps>(WorkflowParameterItemCreate))
);
export { workflowParameterItemCreate as WorkflowParameterItemCreate };
